import { useMemo } from 'react';

import { BidStatus, type ClientBid, type ClientFilterType, type User } from '../types';

export interface UseClientFiltersResult {
  filteredUsers: User[];
  filterCounts: Record<ClientFilterType, number>;
}

/**
 * Pure client filtering — port of the web useClientFilters (which read the
 * filter/search from the URL). Here filter + search are passed in from the page
 * (admin holds them as local state); bids drive the "active bids" filter/count.
 */
export function useClientFilters(
  users: User[],
  bids: ClientBid[],
  filter: ClientFilterType,
  search: string | undefined,
): UseClientFiltersResult {
  return useMemo(() => {
    if (!users.length) {
      return {
        filteredUsers: [],
        filterCounts: { all: 0, verified: 0, unverified: 0, activeBids: 0 },
      };
    }

    const validUsers = users.filter((u): u is User & { _id: string } => !!u._id);

    const usersWithActiveBids = new Set<string>();
    for (const bid of bids) {
      if (bid.status === BidStatus.Active || bid.status === BidStatus.Won) {
        usersWithActiveBids.add(bid.userId);
      }
    }

    const filterCounts: Record<ClientFilterType, number> = {
      all: validUsers.length,
      verified: validUsers.filter((u) => u.verified === true).length,
      unverified: validUsers.filter((u) => u.verified !== true).length,
      activeBids: validUsers.filter((u) => usersWithActiveBids.has(u._id)).length,
    };

    let filtered = validUsers;
    switch (filter) {
      case 'verified':
        filtered = validUsers.filter((u) => u.verified === true);
        break;
      case 'unverified':
        filtered = validUsers.filter((u) => u.verified !== true);
        break;
      case 'activeBids':
        filtered = validUsers.filter((u) => usersWithActiveBids.has(u._id));
        break;
      default:
        filtered = validUsers;
    }

    if (search) {
      const term = search.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.firstName?.toLowerCase().includes(term) ||
          user.lastName?.toLowerCase().includes(term) ||
          user.email?.toLowerCase().includes(term) ||
          user.displayName?.toLowerCase().includes(term) ||
          user.phone?.toLowerCase().includes(term),
      );
    }

    return { filteredUsers: filtered, filterCounts };
  }, [users, bids, filter, search]);
}
